import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

/**
 * useFocusOnRouteChange Hook
 * Moves focus to main content after route changes initiated by keyboard navigation
 * Only triggers when navigation was performed via keyboard (not mouse)
 * Improves accessibility for keyboard and screen reader users
 */
export function useFocusOnRouteChange() {
	const location = useLocation();
	const previousPathRef = useRef(location.pathname);
	const keyboardNavigationRef = useRef(false);

	useEffect(() => {
		// Track if last interaction was keyboard-based
		const handleKeyDown = (e: KeyboardEvent) => {
			// Enter or Space on links/buttons indicates keyboard navigation
			if (e.key === "Enter" || e.key === " ") {
				keyboardNavigationRef.current = true;
			}
		};

		const handleMouseDown = () => {
			keyboardNavigationRef.current = false;
		};

		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("mousedown", handleMouseDown);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("mousedown", handleMouseDown);
		};
	}, []);

	useEffect(() => {
		// Only focus if route changed and navigation was via keyboard
		if (location.pathname !== previousPathRef.current && keyboardNavigationRef.current) {
			const mainContent = document.getElementById("main-content");
			if (mainContent) {
				// Set tabindex temporarily to make element focusable
				mainContent.setAttribute("tabindex", "-1");
				mainContent.focus();
				// Remove tabindex after focus to avoid affecting tab order
				mainContent.addEventListener(
					"blur",
					() => {
						mainContent.removeAttribute("tabindex");
					},
					{ once: true },
				);
			}
			keyboardNavigationRef.current = false;
		}

		previousPathRef.current = location.pathname;
	}, [location.pathname]);
}
